import React from 'react';
import { APP_TEXT } from '@/constant/theme/colors.constant';
import { APP_HEX } from '@/constant/theme/hex.constant';
import { Text } from './AppText';

export interface StorageRingProps {
  /** Storage usage percentage (0–100) */
  pct: number;
  /** Overall size in px (default 72) */
  size?: number;
}

/**
 * StorageRing — circular SVG donut showing a storage usage percentage.
 *
 * Colour transitions automatically:
 *  - ≤ 60 %  → sky blue
 *  - 61–80 % → amber
 *  - > 80 %  → red (critical)
 *
 * Usage:
 * ```tsx
 * <StorageRing pct={72} size={96} />
 * ```
 */
export const StorageRing = ({ pct, size = 72 }: StorageRingProps) => {
  const r = size / 2 - 7;
  const circ = 2 * Math.PI * r;
  const fill = (pct / 100) * circ;
  const color =
    pct > 80 ? APP_HEX.danger : pct > 60 ? APP_HEX.warning : APP_HEX.sky;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(150,150,150,0.1)"
          strokeWidth="7"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeDasharray={`${fill} ${circ - fill}`}
          strokeLinecap="round"
          style={{
            filter: `drop-shadow(0 0 5px ${color}80)`,
            transition: 'stroke-dasharray 0.8s ease',
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="text-[13px] font-bold tabular-nums leading-none"
          style={{ color }}
        >
          {pct}%
        </span>
        <span
          className={`text-[9px] uppercase tracking-widest leading-none mt-0.5 ${APP_TEXT.faint}`}
        >
          used
        </span>
      </div>
    </div>
  );
};
